import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate, daysFromNow } from "../lib/api";
import Topbar from "../components/Topbar";
import KpiCard from "../components/KpiCard";
import StatusBadge from "../components/StatusBadge";
import ExpenseCategoryBadge from "../components/ExpenseCategoryBadge";
import EmptyState from "../components/EmptyState";
import { useAuth } from "../lib/auth";
import { Plus, Search, Wallet, AlertTriangle, RefreshCcw } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const STATUS_FILTERS = {
  corporate_admin: ["pending_approval", "approved", "paid", "rejected", "all"],
  branch_manager: ["pending_approval", "approved", "paid", "rejected", "all"],
  treasury: ["approved", "paid", "pending_approval", "all"],
};

const STATUS_TAB_LABEL_KEYS = {
  pending_approval: "expenses.tabPendingApproval",
  approved: "expenses.tabApproved",
  paid: "expenses.tabPaid",
  rejected: "expenses.tabRejected",
  all: "expenses.tabAll",
};

export default function OperatingExpenses() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("pending_approval");
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);

  const load = () => {
    api.get("/operating-expenses").then((r) => setList(r.data || [])).catch(() => {});
    api.get("/operating-expenses/stats/summary").then((r) => setSummary(r.data)).catch(() => setSummary({ pending_count: 0, approved_count: 0, paid_this_month: 0, recurring_templates: 0 }));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user?.role === "treasury") setFilter("approved");
  }, [user?.role]);

  const tabs = STATUS_FILTERS[user?.role] || STATUS_FILTERS.corporate_admin;
  const labelFor = (f) => {
    if (user?.role === "treasury" && f === "approved") return t("expenses.tabPendingTreasury");
    if (user?.role === "treasury" && f === "pending_approval") return t("expenses.tabWaitingApproval");
    return t(STATUS_TAB_LABEL_KEYS[f]);
  };

  const filtered = list.filter((e) => {
    if (filter !== "all" && e.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !e.folio?.toLowerCase().includes(q) &&
        !e.vendor_name?.toLowerCase().includes(q) &&
        !e.description?.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const dueChip = (e) => {
    if (e.status === "paid") return null;
    const d = daysFromNow(e.due_date);
    if (d === null) return null;
    if (d < 0) return <span className="text-[10px] font-bold text-red-700">· {t("expenses.dueOverdue", { n: Math.abs(d) })}</span>;
    if (d === 0) return <span className="text-[10px] font-bold text-amber-700">· {t("expenses.dueToday")}</span>;
    if (d <= 3) return <span className="text-[10px] font-bold text-amber-700">· {t("expenses.dueIn", { n: d })}</span>;
    return null;
  };

  return (
    <>
      <Topbar title={t("expenses.title")} subtitle={t("expenses.subtitle")} />
      <div className="p-6 space-y-5">
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard testId="kpi-exp-pending" label={t("expenses.kpiPending")} value={summary.pending_count} icon={AlertTriangle} accent={summary.pending_count > 0} />
            <KpiCard testId="kpi-exp-approved" label={t("expenses.kpiApproved")} value={summary.approved_count} icon={Wallet} />
            <KpiCard testId="kpi-exp-paid-month" label={t("expenses.kpiPaidMonth")} value={fmtMXN(summary.paid_this_month || 0)} icon={Wallet} />
            <button
              data-testid="exp-recurring-shortcut"
              onClick={() => navigate("/gastos/recurrentes")}
              className="bg-white border border-slate-200 hover:border-[#FFDF00] hover:bg-[#FFFBE6] rounded-2xl p-4 text-left transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("expenses.kpiTemplates")}</span>
                <RefreshCcw className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="font-display font-extrabold text-2xl text-[#1A1A1A]">{summary.recurring_templates || 0}</div>
              <div className="text-[11px] text-slate-500 mt-1">{t("expenses.kpiRecurring")}<br />{t("expenses.kpiRecurringSub")}</div>
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {(user?.role === "branch_manager" || user?.role === "corporate_admin") && (
            <button data-testid="exp-new-btn" onClick={() => navigate("/gastos/nuevo")}
              className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" strokeWidth={2.5} /> {t("expenses.newExpense")}
            </button>
          )}
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="exp-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("expenses.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 flex-wrap">
            {tabs.map((f) => (
              <button key={f} data-testid={`exp-filter-${f}`} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${filter === f ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {labelFor(f)}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            title={t("expenses.emptyTitle")}
            message={user?.role === "branch_manager" || user?.role === "corporate_admin"
              ? t("expenses.emptyMsgCreate") : t("expenses.emptyMsgView")}
            icon={Wallet}
          />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.folio")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.category")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("expenses.tableProvider")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("expenses.tableBranch")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.total")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("expenses.tableDue")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e, idx) => (
                    <motion.tr key={e.id} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                      onClick={() => navigate(`/gastos/${e.id}`)}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                      data-testid={`exp-row-${e.folio}`}>
                      <td className="px-6 py-3.5 font-mono text-sm font-bold text-[#1A1A1A]">{e.folio}</td>
                      <td className="px-6 py-3.5"><ExpenseCategoryBadge category={e.category} subcategory={e.subcategory} /></td>
                      <td className="px-6 py-3.5 text-sm">
                        <div className="font-semibold">{e.vendor_name}</div>
                        <div className="text-xs text-slate-500 max-w-[260px] truncate">{e.description}</div>
                      </td>
                      <td className="px-6 py-3.5 text-sm">{e.branch_name || <span className="text-slate-400 italic">— Corp —</span>}</td>
                      <td className="px-6 py-3.5 text-sm font-bold text-right">{fmtMXN(e.total)}</td>
                      <td className="px-6 py-3.5 text-xs">
                        <div className="text-slate-700 font-semibold">{fmtDate(e.due_date)}</div>
                        {dueChip(e)}
                      </td>
                      <td className="px-6 py-3.5"><StatusBadge status={e.status} /></td>
                      <td className="px-6 py-3.5 text-right">
                        <span className="text-[11px] font-bold text-slate-400 hover:text-[#1A1A1A]">{t("expenses.viewDetail")}</span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {user?.role === "corporate_admin" && filtered.some((e) => e.status === "pending_approval") && (
          <div className="bg-gradient-to-br from-[#FFFBE6] to-white border border-[#FFDF00] rounded-2xl p-4 text-xs text-slate-700">
            <span className="font-bold">{t("common.tipFast")}:</span> {t("expenses.corpHint")}
          </div>
        )}
        {user?.role === "treasury" && filtered.some((e) => e.status === "approved") && (
          <div className="bg-gradient-to-br from-[#FFFBE6] to-white border border-[#FFDF00] rounded-2xl p-4 text-xs text-slate-700">
            <span className="font-bold">{t("common.tipFast")}:</span> {t("expenses.treasuryHint")}
          </div>
        )}
      </div>
    </>
  );
}
